<template>
  <card shadow>
    <template v-slot:header>
      <div class="row align-items-center">
        <div class="col">
          <h3 class="mb-0">Tip Breakdown</h3>
          <p class="text-muted text-sm mb-0">
            Click on a tip to see how it was split
          </p>
        </div>
        <div class="col-auto d-flex align-items-center">
          <input
            type="text"
            v-model="searchQuery"
            class="form-control form-control-sm mr-3"
            placeholder="Search by tip ID, location, recipient..."
            style="width: 280px"
          />
          <button class="btn btn-sm btn-success" @click="exportData">
            <i class="fas fa-download mr-1"></i>Export
          </button>
        </div>
      </div>
    </template>

    <div class="table-responsive">
      <table class="table align-items-center table-flush table-hover">
        <thead class="thead-light">
          <tr>
            <sortable-header
              sort-key="tipDateFormatted"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Date/Time
            </sortable-header>
            <th>Tip ID</th>
            <sortable-header
              sort-key="locationName"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Location
            </sortable-header>
            <sortable-header
              sort-key="tipAmount"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Tip Amount
            </sortable-header>
            <sortable-header
              sort-key="recipientCount"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Recipients
            </sortable-header>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="charge in paginatedData"
            :key="charge.tipId"
            :class="charge.unmatched ? 'table-warning' : ''"
            class="clickable-row"
            @click="$emit('select', charge)"
          >
            <td>{{ charge.tipDateFormatted }}</td>
            <td>
              <copy-text :text="charge.tipId" label="Tip ID" inline>
                <code class="text-monospace">{{
                  truncateId(charge.tipId)
                }}</code>
              </copy-text>
            </td>
            <td>{{ charge.locationName }}</td>
            <td>
              <strong>{{ charge.tipAmountFormatted }}</strong>
            </td>
            <td>
              <span
                :class="
                  charge.recipientCount > 0 ? 'text-success' : 'text-warning'
                "
              >
                {{ charge.recipientCount }}
              </span>
            </td>
            <td>
              <reason-badge
                :reason="charge.unmatched ? 'unmatched' : 'matched'"
              />
            </td>
            <td>
              <button
                class="btn btn-sm btn-outline-primary"
                @click.stop="$emit('select', charge)"
              >
                <i class="fas fa-search"></i> Details
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <table-pagination
      v-model:current-page="currentPage"
      :total-items="filteredData.length"
      :per-page="perPage"
      item-label="tips"
    />
  </card>
</template>

<script>
import CopyText from "@/components/CopyText";
import TablePagination from "@/components/TablePagination";
import SortableHeader from "@/components/SortableHeader";
import ReasonBadge from "@/components/ReasonBadge";
import { truncateId, exportToCSV } from "@/utils/formatters";

export default {
  name: "ChargesTab",
  components: {
    CopyText,
    TablePagination,
    SortableHeader,
    ReasonBadge,
  },
  props: {
    charges: {
      type: Array,
      required: true,
    },
    reportId: {
      type: String,
      required: true,
    },
  },
  emits: ["select"],
  data() {
    return {
      searchQuery: "",
      currentPage: 1,
      perPage: 15,
      sortState: { key: "tipDateFormatted", order: "desc" },
    };
  },
  computed: {
    filteredData() {
      let result = [...this.charges];

      // Search filter
      if (this.searchQuery) {
        const search = this.searchQuery.toLowerCase();
        result = result.filter((item) => {
          if (item.tipId?.toLowerCase().includes(search)) return true;
          if (item.locationName?.toLowerCase().includes(search)) return true;
          if (
            item.recipients?.some(
              (r) =>
                r.name?.toLowerCase().includes(search) ||
                r.email?.toLowerCase().includes(search) ||
                r.staffId?.toLowerCase().includes(search)
            )
          )
            return true;
          return false;
        });
      }

      // Sort
      result.sort((a, b) => {
        let aVal = a[this.sortState.key];
        let bVal = b[this.sortState.key];

        if (
          this.sortState.key === "tipAmount" ||
          this.sortState.key === "recipientCount"
        ) {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        }

        if (aVal < bVal) return this.sortState.order === "asc" ? -1 : 1;
        if (aVal > bVal) return this.sortState.order === "asc" ? 1 : -1;
        return 0;
      });

      return result;
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredData.slice(start, start + this.perPage);
    },
  },
  methods: {
    truncateId,
    handleSort(key) {
      if (this.sortState.key === key) {
        this.sortState.order = this.sortState.order === "asc" ? "desc" : "asc";
      } else {
        this.sortState.key = key;
        this.sortState.order = "asc";
      }
    },
    exportData() {
      exportToCSV({
        data: this.filteredData,
        columns: [
          { header: "Tip ID", value: "tipId" },
          { header: "Date", value: "tipDateFormatted" },
          { header: "Location", value: "locationName" },
          {
            header: "Tip Amount",
            value: (c) => (c.tipAmount / 100).toFixed(2),
          },
          { header: "Recipients", value: "recipientCount" },
          {
            header: "Status",
            value: (c) => (c.unmatched ? "Unmatched" : "Matched"),
          },
        ],
        filename: `tip-breakdown-${this.reportId}.csv`,
      });
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.clickable-row {
  cursor: pointer;

  &:hover {
    background-color: #f8f9fe !important;
  }
}

.text-monospace {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  font-size: 0.8rem;
}
</style>
